import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  loading?: boolean;
}

const VARIANTS = {
  primary:
    'bg-brand text-white hover:bg-brand-600 focus-visible:ring-brand shadow-sm dark:bg-brand dark:hover:bg-brand-600',
  secondary:
    'border border-slate-200 bg-white text-ink-2 hover:bg-slate-50 focus-visible:ring-brand shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700',
  ghost:
    'text-ink-2 hover:bg-slate-100 hover:text-ink focus-visible:ring-brand dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white',
  danger:
    'bg-danger text-white hover:bg-pink-800 focus-visible:ring-danger shadow-sm dark:bg-danger dark:hover:bg-pink-800',
  success:
    'bg-success text-white hover:bg-emerald-600 focus-visible:ring-success shadow-sm dark:bg-success dark:hover:bg-emerald-600',
};

const SIZES = {
  // min-h (not fixed h) so a long label can wrap to two lines inside narrow
  // cards instead of spilling past the button's border.
  xs: 'min-h-7 py-1 px-2.5 text-[11px] rounded-lg gap-1.5',
  sm: 'min-h-8 py-1 px-3 text-xs rounded-lg gap-1.5',
  md: 'min-h-9 py-1.5 px-4 text-[13px] rounded-lg gap-2',
  lg: 'min-h-10 py-2 px-5 text-sm rounded-lg gap-2.5',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    variant = 'secondary',
    size = 'md',
    leadingIcon,
    trailingIcon,
    loading = false,
    disabled,
    className,
    type = 'button',
    ...props
  },
  ref,
) {
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      className={cn(
        'inline-flex max-w-full items-center justify-center font-bold transition-colors select-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        SIZES[size],
        VARIANTS[variant],
        className,
      )}
      {...props}
    >
      {loading ? (
        <svg
          className="animate-spin h-4 w-4 shrink-0"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : leadingIcon ? (
        <span className="shrink-0" aria-hidden>
          {leadingIcon}
        </span>
      ) : null}
      {/* Callers often put an icon inside children (`<PlusIcon className="me-1.5" />
          Label`). Tailwind preflight makes <svg> display:block, so a plain span
          stacked the icon above the label. Icon stays beside the text; the
          text itself may wrap (never overflow the button). */}
      {children ? (
        <span className="inline-flex min-w-0 max-w-full items-center justify-center gap-1.5 text-center leading-tight [&>svg]:shrink-0 [&>svg]:!m-0">
          {children}
        </span>
      ) : null}
      {!loading && trailingIcon ? (
        <span className="shrink-0" aria-hidden>
          {trailingIcon}
        </span>
      ) : null}
    </button>
  );
});

export default Button;
